import type { LanguageType } from '@/types/language';

import {
  HOME_HERO_COPY_BY_LOCALE,
  type HomeHeroMetricId,
  type HomeHeroNarrativePointId,
} from './homeHeroCopy';

/**
 * ### HomeProofStatId
 *
 * One entry of the proof section: the three press-kit metrics followed by
 * the three narrative answers, in the order they are scrolled through.
 */
export type HomeProofStatId = HomeHeroMetricId | HomeHeroNarrativePointId;

/**
 * ### HomeProofStatFormat
 *
 * How a figure's number prints. Intl's options, less the scientific and
 * engineering notations: no press-kit figure is printed that way, and the
 * counter that renders these only accepts compact or standard.
 */
export type HomeProofStatFormat = Omit<Intl.NumberFormatOptions, 'notation'> & {
  notation?: 'compact' | 'standard';
};

/**
 * ### HomeProofStat
 *
 * A press-kit figure prepared for the counting display. The copy itself
 * comes from {@link HOME_HERO_COPY_BY_LOCALE}; this module only adds the
 * number behind each figure and how to print it, so the figure that counts
 * up on the left and the sentence on the right are the same fact.
 */
export type HomeProofStat = {
  /** Stable identifier, used as key, for the icon, and for the scroll spy */
  id: HomeProofStatId;

  /**
   * Short label over the figure. The press-kit metric names are English set
   * in caps, so the entries drawn from the narrative take the same form
   * rather than mixing scripts down the column.
   */
  kicker: string;

  /** Optional entry title, distinct from the sticky figure label. */
  entryTitle?: string;

  /** The number the figure is made of, for the counter */
  value: number;

  /** How the number prints, as Intl.NumberFormat options for the locale */
  format: HomeProofStatFormat;

  /** Text printed after the number, part of the figure */
  suffix: string;

  /**
   * The figure as text, exactly what value + format + suffix produce. Kept
   * written out so a test can hold the counter to the press-kit figure.
   */
  figure: string;

  /** The press-kit sentence the figure rests on */
  description: string;

  /**
   * What the figure counts, or its scope. A figure measured over a narrower
   * range than the sentence beside it carries that range here.
   */
  note?: string;

  /**
   * The press-kit figures that sit under this one: what the headline number
   * breaks down into, or the range it was measured across. Three at most,
   * because they are read at a glance rather than studied.
   */
  details: ReadonlyArray<HomeProofDetail>;
};

/**
 * ### HomeProofDetail
 *
 * One supporting figure under a proof entry. Both halves come from the same
 * press kit as the headline figure; a detail never introduces a claim the
 * press kit does not already make.
 */
export type HomeProofDetail = {
  /** The figure itself, short enough to set on one line */
  value: string;

  /** What it counts, in a few words */
  label: string;

  /** Context shown above an equivalence figure. */
  prefix?: string;
};

/** Intl locale tag per site language, for the counter's formatting */
export const HOME_PROOF_STAT_LOCALE: Record<LanguageType, string> = {
  ko: 'ko-KR',
  en: 'en-US',
};

const compact: HomeProofStatFormat = { notation: 'compact' };
const plain: HomeProofStatFormat = {};
const twoDecimals: HomeProofStatFormat = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function pick(locale: LanguageType) {
  const copy = HOME_HERO_COPY_BY_LOCALE[locale];
  const metric = (id: HomeHeroMetricId) => {
    const found = copy.metrics.find((entry) => entry.id === id);
    if (!found) throw new Error(`Missing home metric ${id} for ${locale}`);
    return found;
  };
  const point = (id: HomeHeroNarrativePointId) => {
    const found = copy.narrative.points.find((entry) => entry.id === id);
    if (!found) throw new Error(`Missing narrative point ${id} for ${locale}`);
    return found;
  };

  return { metric, point };
}

const ko = pick('ko');
const en = pick('en');

/**
 * ### HOME_PROOF_STATS_BY_LOCALE
 *
 * The four proof entries per language, in the order they answer the
 * objection above them: the analyses that prove the line was really run,
 * the accuracy of the judgments, the breadth of deployment, and the volume
 * of first-hand data behind it.
 *
 * Four rather than every press-kit figure: the hourly throughput is already
 * stated inside the deployment sentence, and the line-expansion figure is
 * carried as the deployment entry's note, where its narrower inspection
 * scope sits beside the sentence it qualifies.
 *
 * Every number here is one the press-kit copy already states; the copy
 * strings are read from the copy module rather than repeated, so a copy
 * change reaches this section too.
 */
export const HOME_PROOF_STATS_BY_LOCALE: Record<
  LanguageType,
  ReadonlyArray<HomeProofStat>
> = {
  ko: [
    {
      id: 'analyses',
      kicker: 'Analyses',
      value: 100_000_000,
      format: compact,
      suffix: ' 회+',
      figure: ko.point('analyses').figure,
      description: ko.point('analyses').text,
      note: ko.point('analyses').figureCaption,
      details: [
        { value: '양산 라인 품질 검사', label: '양산 라인' },
        { value: '출하 전 최종 검사', label: 'EOL' },
        { value: '설비 예지보전', label: 'PdM' },
      ],
    },
    {
      id: 'accuracy-trust',
      kicker: ko.metric('accuracy-trust').label,
      value: 99.98,
      format: twoDecimals,
      suffix: '%',
      figure: '99.98%',
      description: ko.metric('accuracy-trust').description,
      details: [
        { value: 'SNR 1.77dB', label: '검출한 신호 차이' },
        { value: '99%+', label: '커넥터·볼트 체결' },
        { value: '99.8%', label: 'EOL 불량 유출 차단' },
      ],
    },
    {
      id: 'coverage',
      kicker: 'Deployment',
      value: 30,
      format: plain,
      suffix: '+',
      figure: ko.point('coverage').figure,
      description: ko.point('coverage').text,
      note: ko.metric('accuracy-trust').note,
      details: [
        { value: '6,000개', label: '매 시간 판정' },
        { value: '430만 개', label: '매달 판정' },
        { value: '100% 인라인', label: '전수 판정' },
      ],
    },
    {
      id: 'scale',
      kicker: ko.metric('scale').label,
      value: 520_000,
      // Written out in full: 520,000 lands harder than 52만 for a buyer
      format: plain,
      suffix: '시간',
      figure: '520,000시간',
      description: ko.metric('scale').description,
      note: ko.metric('scale').note,
      details: [
        { value: '20TB', label: '음향 데이터 용량' },
        { value: '178명', label: '검사원 1년 환산' },
        { value: '2년', label: '운영 누적 기준' },
      ],
    },
  ],
  en: [
    {
      id: 'analyses',
      kicker: 'Analyses',
      entryTitle: 'Industrial Solution Applications',
      value: 100_000_000,
      format: compact,
      suffix: '+',
      figure: en.point('analyses').figure,
      description: en.point('analyses').text,
      note: en.point('analyses').figureCaption,
      details: [
        {
          value: 'Production line quality inspection',
          label: 'Production line',
        },
        { value: 'Final inspection before shipment', label: 'EOL' },
        { value: 'Equipment predictive maintenance', label: 'PdM' },
      ],
    },
    {
      id: 'accuracy-trust',
      kicker: en.metric('accuracy-trust').label,
      value: 99.98,
      format: twoDecimals,
      suffix: '%',
      figure: '99.98%',
      description: en.metric('accuracy-trust').description,
      details: [
        { value: 'SNR 1.77dB', label: 'Signal difference detected' },
        { value: '99%+', label: 'Connector and bolt fastening' },
        { value: '99.8%', label: 'EOL Defect Escape Prevention' },
      ],
    },
    {
      id: 'coverage',
      kicker: 'Deployment',
      entryTitle: 'Inspection Statistics',
      value: 30,
      format: plain,
      suffix: '+',
      figure: en.point('coverage').figure,
      description: en.point('coverage').text,
      note: en.metric('accuracy-trust').note,
      details: [
        { value: '6,000', label: 'Products per hour' },
        { value: '4.3 million', label: 'Products per month' },
        { value: '100% in-line', label: 'Inspection & Detection' },
      ],
    },
    {
      id: 'scale',
      kicker: en.metric('scale').label,
      entryTitle: 'Traditional Inspection vs Deeply AI',
      value: 520_000,
      format: plain,
      suffix: ' hours',
      figure: '520,000 hours',
      description: en.metric('scale').description,
      note: en.metric('scale').note,
      details: [
        { value: '20 TB', label: 'Acoustic Data Collected' },
        { value: '2 Years', label: 'Operation' },
        {
          prefix: 'Equivalent to:',
          value: '178 Inspectors',
          label: 'Listening for a year',
        },
      ],
    },
  ],
};
